import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * Auto-resizing textarea. Grows in height to fit its content (no inner
 * scrollbar, no manual drag handle) while never shrinking below `min-h`.
 * Resizes on input and whenever the controlled `value` changes. Consumers can
 * still cap growth by passing e.g. `max-h-40 overflow-y-auto` in className.
 */
const Textarea = React.forwardRef(({ className, value, onChange, onFocus, ...props }, ref) => {
  const innerRef = React.useRef(null)

  const setRefs = React.useCallback((node) => {
    innerRef.current = node
    if (typeof ref === "function") ref(node)
    else if (ref) ref.current = node
  }, [ref])

  const resize = React.useCallback(() => {
    const el = innerRef.current
    if (!el) return
    // Reset first so the textarea can shrink when content is deleted.
    el.style.height = "auto"
    const next = el.scrollHeight
    // When mounted but hidden (e.g. inside a closed collapsible) scrollHeight
    // is 0; keep the CSS min-height instead of collapsing to nothing.
    if (next > 0) el.style.height = `${next}px`
  }, [])

  // Fit on mount and whenever the controlled value changes externally.
  React.useLayoutEffect(() => {
    resize()
  }, [resize, value])

  const handleChange = React.useCallback((e) => {
    resize()
    if (onChange) onChange(e)
  }, [resize, onChange])

  // Re-fit when the field becomes visible/focused (covers textareas that were
  // hidden at mount, so they expand to fit existing content on first focus).
  const handleFocus = React.useCallback((e) => {
    resize()
    if (onFocus) onFocus(e)
  }, [resize, onFocus])

  return (
    <textarea
      className={cn(
        "flex min-h-[60px] w-full resize-none overflow-hidden rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm placeholder:text-muted-foreground/60 placeholder:italic focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      )}
      ref={setRefs}
      value={value}
      onChange={handleChange}
      onFocus={handleFocus}
      {...props} />
  );
})
Textarea.displayName = "Textarea"

export { Textarea }
